/**
 * HydrationCard — daily fluid total against an ACSM-derived personal target,
 * with one-tap quick-add and a trailing 7-day strip.
 *
 * Backend: app/api/hydration.py (mounted at /api/hydration, app/main.py:144).
 *   POST /hydration/log     — body { volume_ml } (0..20000). Date omitted on
 *                             purpose: the server resolves today from
 *                             User.timezone, which the browser must not
 *                             second-guess (server tz != user tz is a known
 *                             bug class in this codebase).
 *   GET  /hydration/today   — running total + target + target_basis
 *   GET  /hydration/window  — ?days=1..30 (silently clamped), gap-filled
 *
 * THE ONE THING TO KNOW: POST /log is an UPSERT of the DAILY TOTAL, not an
 * increment. A "+500" chip therefore posts (current ?? 0) + 500. That also
 * makes every tap destructive-by-overwrite, which is why Undo exists — a
 * mis-tapped +1000 is otherwise only fixable by mental arithmetic.
 *
 * Volumes are millilitres end to end; the /1000 into litres is presentation
 * only and never round-trips to the API.
 *
 * WIRING NOTE for the integration step: `getHydrationToday` / `getHydrationWindow`
 * / `logHydration` belong in src/api/hydration.ts and the exported types in
 * src/types — they live here only so this ships as a single file.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Droplets, Undo2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/api/client";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { useToast } from "@/stores/toast";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Contract types — move to src/types when wired.
// ---------------------------------------------------------------------------

/** How the target was COMPUTED (app/services/hydration_targets.py). Distinct
 *  from `source`, which is where the VOLUME came from. */
export type HydrationTargetBasis = "weight+training" | "weight_only" | "fallback";

export interface HydrationDay {
  date: string;
  hydration_volume_ml: number | null;
  target_volume_ml: number;
  target_basis: HydrationTargetBasis;
  source: string | null;
}

export interface HydrationReading {
  date: string;
  hydration_volume_ml: number | null;
  target_volume_ml: number;
  target_basis: HydrationTargetBasis;
  source: string | null;
}

export interface HydrationWindow {
  end_date: string;
  /** The clamped value the server actually used, not necessarily what we asked for. */
  days: number;
  readings: HydrationReading[];
  avg_volume_ml: number | null;
  days_with_data: number;
}

export interface HydrationLogResult {
  date: string;
  hydration_volume_ml: number;
  source: string;
}

// ---------------------------------------------------------------------------
// API — move to src/api/hydration.ts when wired.
// ---------------------------------------------------------------------------

export async function getHydrationToday(): Promise<HydrationDay> {
  const { data } = await api.get<HydrationDay>("/hydration/today");
  return data;
}

export async function getHydrationWindow(days = 7): Promise<HydrationWindow> {
  const { data } = await api.get<HydrationWindow>("/hydration/window", {
    params: { days },
  });
  return data;
}

/** Posts the DAILY TOTAL, not a delta. `date` is deliberately not accepted —
 *  the server owns "today" via User.timezone. */
export async function logHydration(volumeMl: number): Promise<HydrationLogResult> {
  const { data } = await api.post<HydrationLogResult>("/hydration/log", {
    volume_ml: volumeMl,
  });
  return data;
}

// ---------------------------------------------------------------------------

/** app/schemas/hydration.py:16 — "20L cap per ACSM hyponatremia guidance".
 *  Posting above this is a 422, so the UI stops short of it rather than
 *  letting a chip fail. */
const MAX_VOLUME_ML = 20000;

/** Glass, big glass, bidon, big bidon — the units an athlete actually drinks in. */
const QUICK_ADD_ML = [250, 500, 750, 1000] as const;

const WINDOW_DAYS = 7;

/** Purely a UI advisory. The backend enforces nothing between the target and
 *  the 20 L cap, and grossly overdrinking on a long day is a real sodium risk
 *  for a masters athlete — so the card says something at 1.5x target rather
 *  than cheering an ever-growing number. */
const OVERDRINK_RATIO = 1.5;

/** How long the Undo affordance stays on screen after a tap. */
const UNDO_WINDOW_MS = 15_000;

interface Props {
  /** "full" (default) on /nutrition — target rationale + 7-day strip.
   *  "compact" on Dashboard — total, bar, chips, link out. */
  variant?: "full" | "compact";
}

export function HydrationCard({ variant = "full" }: Props = {}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { show } = useToast();

  /** Total to restore if the athlete undoes the last tap. Null = nothing to
   *  undo. There is no DELETE route, so undoing the first tap of the day
   *  writes 0 rather than removing the row — the closest reversal available. */
  const [undoTo, setUndoTo] = useState<number | null>(null);

  const todayQ = useQuery({
    queryKey: ["hydration-today"],
    queryFn: getHydrationToday,
    staleTime: 20_000,
  });

  const windowQ = useQuery({
    queryKey: ["hydration-window", WINDOW_DAYS],
    queryFn: () => getHydrationWindow(WINDOW_DAYS),
    staleTime: 60_000,
    enabled: variant === "full",
  });

  const logMut = useMutation({
    mutationFn: (input: { volumeMl: number; previous: number | null }) =>
      logHydration(input.volumeMl),
    onSuccess: async (_data, input) => {
      setUndoTo(input.previous ?? 0);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["hydration-today"] }),
        qc.invalidateQueries({ queryKey: ["hydration-window"] }),
        // Not obvious: POST /log stamps wellness_entries.synced_at, which is
        // what Settings -> SyncStatusCard renders as "last wellness sync".
        qc.invalidateQueries({ queryKey: ["sync-status"] }),
      ]);
    },
    onError: () => {
      show({
        message: t("hydration.log_failed", "Could not save that. Try again."),
        variant: "error",
      });
    },
  });

  const undoMut = useMutation({
    mutationFn: (volumeMl: number) => logHydration(volumeMl),
    onSuccess: async () => {
      setUndoTo(null);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["hydration-today"] }),
        qc.invalidateQueries({ queryKey: ["hydration-window"] }),
        qc.invalidateQueries({ queryKey: ["sync-status"] }),
      ]);
    },
    onError: () => {
      show({
        message: t("hydration.undo_failed", "Could not undo. Try again."),
        variant: "error",
      });
    },
  });

  // An undo offer that outlives its context is worse than none — it would put
  // back a total from an hour ago.
  useEffect(() => {
    if (undoTo === null) return;
    const id = window.setTimeout(() => setUndoTo(null), UNDO_WINDOW_MS);
    return () => window.clearTimeout(id);
  }, [undoTo]);

  const nfLitres = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const litres = (ml: number): string => nfLitres.format(ml / 1000);

  // --- 1. loading -----------------------------------------------------------
  if (todayQ.isLoading) {
    return (
      <Card data-testid="hydration-loading">
        <SectionLabel>{t("hydration.section_label", "Hydration")}</SectionLabel>
        <div className="mt-3 space-y-3">
          <Skeleton
            className="h-8 w-40"
            label={t("hydration.loading", "Loading hydration")}
          />
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      </Card>
    );
  }

  // --- 2. error -------------------------------------------------------------
  if (todayQ.isError || !todayQ.data) {
    return (
      <Card data-testid="hydration-error">
        <SectionLabel>{t("hydration.section_label", "Hydration")}</SectionLabel>
        <EmptyState
          variant="hero"
          icon={Droplets}
          title={t("hydration.load_failed_title", "Hydration is unavailable")}
          description={t(
            "hydration.load_failed_desc",
            "Today's fluid total could not be loaded.",
          )}
        >
          <Button variant="secondary" size="sm" onClick={() => void todayQ.refetch()}>
            {t("hydration.retry", "Try again")}
          </Button>
        </EmptyState>
      </Card>
    );
  }

  const day = todayQ.data;
  const logged = day.hydration_volume_ml;
  const current = logged ?? 0;
  const target = day.target_volume_ml;

  // --- 3. empty (no row yet — what every new athlete sees) -------------------
  const isEmpty = logged === null;

  const pct = target > 0 ? (current / target) * 100 : 0;
  // Only when the target is actually this athlete's. A "fallback" basis is a
  // flat 2.5 L default that the card itself labels as generic, and a
  // blood-sodium warning measured against a guess is both unfounded and
  // training the athlete to dismiss the one that is founded. A 95 kg athlete
  // after four hours of training clears 2.5 L on an ordinary day.
  const targetIsPersonal = day.target_basis !== "fallback";
  const overdrinking =
    targetIsPersonal && target > 0 && current >= target * OVERDRINK_RATIO;
  const headroom = Math.max(0, MAX_VOLUME_ML - current);

  const [basisKey, basisDefault] = basisCopy(day.target_basis);
  const isFallbackTarget = !targetIsPersonal;

  const busy = logMut.isPending || undoMut.isPending;
  const isCompact = variant === "compact";

  const quickAdd = (deltaMl: number) => {
    if (busy) return;
    const next = Math.min(MAX_VOLUME_ML, current + deltaMl);
    logMut.mutate({ volumeMl: next, previous: logged });
  };

  return (
    <Card data-testid="hydration-card">
      <div className="flex items-center justify-between">
        <SectionLabel>{t("hydration.section_label", "Hydration")}</SectionLabel>
        {isCompact ? (
          <Link
            to="/nutrition"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-dark hover:underline"
          >
            {t("hydration.details", "Details")}
            <ArrowRight className="h-3 w-3" strokeWidth={2} />
          </Link>
        ) : (
          <Droplets className="h-4 w-4 text-taupe" strokeWidth={1.8} aria-hidden="true" />
        )}
      </div>

      {/* Running total vs target */}
      <div className="mt-3 flex items-baseline gap-2">
        <span
          className={cn(
            "metric-number",
            isCompact ? "text-[26px]" : "text-[32px]",
            isEmpty && "text-taupe",
          )}
          data-testid="hydration-total"
        >
          {litres(current)}
        </span>
        <span className={cn("text-taupe", isCompact ? "text-[13px]" : "text-[15px]")}>
          / {litres(target)} {t("hydration.litres_short", "L")}
        </span>
      </div>

      {isEmpty ? (
        <p className="mt-1 text-[12px] text-taupe" data-testid="hydration-empty">
          {t(
            "hydration.empty_today",
            "Nothing logged today yet — tap a size below after your next drink.",
          )}
        </p>
      ) : (
        <p className="mt-1 text-[12px] text-taupe">
          {current < target
            ? t("hydration.left_to_go", "{{n}} L to go", {
                n: litres(target - current),
              })
            : current === target
              ? t("hydration.target_exact", "Target met.")
              : t("hydration.target_met", "Target met — {{n}} L over", {
                  n: litres(current - target),
                })}
        </p>
      )}

      {/* Progress. Stays amber past 100%: exceeding a hydration target is not
          a failure state, so the coral over-shoot treatment used by
          CalorieBalanceCard would say the wrong thing here. */}
      {/* aria-valuenow is clamped to the maximum below: past target the bar
          stays full and amber by design, and announcing a value above the
          maximum would contradict it. The overshoot is stated in the text
          above, which is where a screen-reader user gets it. */}
      <div className="mt-3">
        <div
          className="h-2 overflow-hidden rounded-full bg-cream-border"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={target}
          aria-valuenow={Math.min(current, target)}
          aria-label={t("hydration.progress_aria", "Fluid intake against target")}
        >
          <div
            className="h-full rounded-full bg-amber transition-all"
            style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
          />
        </div>
        {!isCompact ? (
          <p className="mt-1.5 text-[11px] text-taupe">
            {t("hydration.target_basis_prefix", "Target from")}{" "}
            {t(basisKey, basisDefault)}
          </p>
        ) : null}
      </div>

      {/* The target is not personal here — say so rather than dressing a flat
          2500 ml default up as a computed number. */}
      {isFallbackTarget && !isCompact ? (
        <p
          className="mt-2 rounded-md border border-cream-border bg-cream p-2.5 text-[11px] leading-relaxed text-taupe"
          data-testid="hydration-fallback-note"
        >
          {t(
            "hydration.fallback_note",
            "This is a generic 2.5 L estimate, not your target — add your weight in the nutrition profile and it becomes personal.",
          )}
        </p>
      ) : null}

      {overdrinking ? (
        <p
          className="mt-2 inline-flex items-start gap-1.5 text-[11px] leading-relaxed text-coral"
          role="status"
          data-testid="hydration-overdrink-note"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} aria-hidden="true" />
          {t(
            "hydration.overdrink_note",
            "Well past today's target. On a long day, drinking far beyond your needs without salt can drop blood sodium — take some with electrolytes.",
          )}
        </p>
      ) : null}

      {/* One-tap add */}
      <div className="mt-3 grid grid-cols-4 gap-2">
        {QUICK_ADD_ML.map((ml) => (
          <Button
            key={ml}
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            disabled={busy || ml > headroom}
            onClick={() => quickAdd(ml)}
            aria-label={t("hydration.add_aria", "Add {{n}} millilitres", { n: ml })}
            data-testid={`hydration-add-${ml}`}
          >
            +{ml}
          </Button>
        ))}
      </div>

      <div className="mt-2 flex min-h-[18px] items-center justify-between text-[11px]">
        <span className="text-taupe">
          {t("hydration.quick_add_unit", "ml per tap")}
        </span>
        {undoTo !== null ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => undoMut.mutate(undoTo)}
            className="inline-flex items-center gap-1 font-medium text-amber-dark hover:underline disabled:opacity-50"
            data-testid="hydration-undo"
          >
            <Undo2 className="h-3 w-3" strokeWidth={2} aria-hidden="true" />
            {t("hydration.undo", "Undo")}
          </button>
        ) : null}
      </div>

      {/* Trailing 7 days */}
      {!isCompact ? (
        <div className="mt-4 border-t border-cream-border pt-3">
          {windowQ.isLoading ? (
            <Skeleton
              className="h-16 w-full"
              label={t("hydration.window_loading", "Loading the last 7 days")}
            />
          ) : windowQ.isError || !windowQ.data ? (
            <div className="flex items-center justify-between gap-3">
              <EmptyState
                variant="inline"
                title={t("hydration.window_failed", "The last 7 days could not be loaded.")}
              />
              <button
                type="button"
                onClick={() => void windowQ.refetch()}
                className="shrink-0 text-[11px] font-medium text-amber-dark hover:underline"
              >
                {t("hydration.retry", "Try again")}
              </button>
            </div>
          ) : (
            <HydrationWindowStrip
              data={windowQ.data}
              litres={litres}
              language={i18n.language}
            />
          )}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------

/** Per-day bars with a per-day dashed target line. The target genuinely moves
 *  with training load, so a single flat line across the strip would be a lie. */
function HydrationWindowStrip({
  data,
  litres,
  language,
}: {
  data: HydrationWindow;
  litres: (ml: number) => string;
  language: string;
}) {
  const { t } = useTranslation();

  const ceiling = data.readings.reduce(
    (max, r) => Math.max(max, r.target_volume_ml, r.hydration_volume_ml ?? 0),
    1,
  );

  const dayLabel = new Intl.DateTimeFormat(language, { weekday: "short" });

  return (
    <div data-testid="hydration-window">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-wider text-taupe">
          {/* `n`, not `count` — `count` would send i18next into plural-key
              resolution and need _one/_other variants in every locale. */}
          {t("hydration.last_n_days", "Last {{n}} days", { n: data.days })}
        </span>
        <span className="text-[11px] text-taupe">
          {data.avg_volume_ml === null
            ? t("hydration.window_empty", "No days logged yet")
            : t("hydration.avg_inline", "avg {{n}} L", {
                n: litres(data.avg_volume_ml),
              })}
        </span>
      </div>

      <div className="mt-2 flex items-end gap-1.5">
        {data.readings.map((r) => {
          const vol = r.hydration_volume_ml;
          const hit = vol !== null && vol >= r.target_volume_ml;
          const barPct = vol === null ? 0 : (vol / ceiling) * 100;
          const targetPct = (r.target_volume_ml / ceiling) * 100;
          const dow = dayLabel.format(new Date(`${r.date}T00:00:00`));

          return (
            <div key={r.date} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="relative h-16 w-full rounded-sm bg-cream"
                title={
                  vol === null
                    ? t("hydration.bar_title_empty", "{{date}} — nothing logged", {
                        date: r.date,
                      })
                    : t("hydration.bar_title", "{{date}} — {{n}} of {{target}} L", {
                        date: r.date,
                        n: litres(vol),
                        target: litres(r.target_volume_ml),
                      })
                }
              >
                {/* dashed per-day target */}
                <div
                  className="absolute inset-x-0 border-t border-dashed border-taupe/50"
                  style={{ bottom: `${Math.min(100, targetPct)}%` }}
                  aria-hidden="true"
                />
                {vol === null ? (
                  <div
                    className="absolute inset-x-1 bottom-0 h-[3px] rounded-sm bg-cream-border"
                    aria-hidden="true"
                  />
                ) : (
                  <div
                    className={cn(
                      "absolute inset-x-0 bottom-0 rounded-sm transition-all",
                      hit ? "bg-amber" : "bg-navy",
                    )}
                    style={{ height: `${Math.max(3, Math.min(100, barPct))}%` }}
                    aria-hidden="true"
                  />
                )}
              </div>
              <span className="text-[9px] uppercase tracking-wider text-taupe">
                {dow}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** `target_basis` is the honesty field — surface it, do not hide it. Returns
 *  the key/default pair rather than a string so the caller owns `t` and this
 *  stays free of i18next's TFunction generics. */
function basisCopy(basis: HydrationTargetBasis): [string, string] {
  switch (basis) {
    case "weight+training":
      return ["hydration.basis_weight_training", "your weight + today's training"];
    case "weight_only":
      return ["hydration.basis_weight_only", "your weight (no session planned)"];
    case "fallback":
      return ["hydration.basis_fallback", "a generic estimate"];
    default:
      return ["hydration.basis_unknown", "an estimate"];
  }
}
